import * as React from 'react';

import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

/**
 * Renders `text` on a single truncated line and shows a tooltip with the full
 * value ONLY when the text actually overflows its container. Overflow is
 * re-evaluated on text and size changes (e.g. when a kanban column resizes).
 */
export function EllipsisTooltip({ text, className }: { text: string; className?: string }) {
    const ref = React.useRef<HTMLSpanElement>(null);
    const [isOverflowing, setIsOverflowing] = React.useState(false);

    React.useLayoutEffect(() => {
        const element = ref.current;
        if (!element) return;

        const updateOverflow = () => setIsOverflowing(element.scrollWidth > element.clientWidth);
        updateOverflow();

        const observer = new ResizeObserver(updateOverflow);
        observer.observe(element);
        return () => observer.disconnect();
    }, [text]);

    return (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild>
                    <span ref={ref} className={cn('block truncate', className)}>
                        {text}
                    </span>
                </TooltipTrigger>
                {isOverflowing ? <TooltipContent>{text}</TooltipContent> : null}
            </Tooltip>
        </TooltipProvider>
    );
}
